import { z } from "zod";

export const locales = ["de", "en"] as const;
export const roles = ["owner", "admin", "editor", "reporter"] as const;
export const eventStatuses = [
  "scheduled",
  "hidden",
  "live",
  "paused",
  "archived",
  "removed",
] as const;
export const postStates = [
  "draft",
  "scheduled",
  "published",
  "unpublished",
  "pending",
] as const;
export const postTypes = ["text", "image", "embed", "video", "system"] as const;
export const mediaStates = ["queued", "processing", "ready", "failed"] as const;
export const videoStates = [
  "uploading",
  "queued",
  "processing",
  "ready",
  "failed",
  "removed",
] as const;
export const submissionStates = ["pending", "approved", "rejected"] as const;
export const timelineOrders = ["newest", "oldest"] as const;
export const watermarkPositions = [
  "northwest",
  "north",
  "northeast",
  "west",
  "center",
  "east",
  "southwest",
  "south",
  "southeast",
] as const;

export const embedAllowedHosts = new Set([
  "youtube.com",
  "www.youtube.com",
  "youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "vimeo.com",
  "www.vimeo.com",
  "player.vimeo.com",
  "open.spotify.com",
  "soundcloud.com",
  "www.soundcloud.com",
  "instagram.com",
  "www.instagram.com",
  "x.com",
  "www.x.com",
  "twitter.com",
  "www.twitter.com",
  "mobile.twitter.com",
  "mixlr.com",
  "www.mixlr.com",
  "events.mixlr.com",
  "player.mediadelivery.net",
  "iframe.mediadelivery.net",
]);

export const embedProviderLabels = [
  "YouTube",
  "Vimeo",
  "Spotify",
  "SoundCloud",
  "Instagram",
  "X (Twitter)",
  "Mixlr",
  "Bunny Stream",
] as const;

export const embedProviderDisplayNames: Record<string, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  spotify: "Spotify",
  soundcloud: "SoundCloud",
  instagram: "Instagram",
  x: "X",
  mixlr: "Mixlr",
  bunny: "Bunny Stream",
};

export function embedProviderDisplayName(provider: string): string {
  return embedProviderDisplayNames[provider] ?? provider;
}

// Formate der Embed-Provider (Stand September 2026):
// - Instagram: { type: "MEASURE", details: { height } }
// - X/Twitter: { "twttr.embed": [{ method: "twttr.private.resize", params: [{ height }] }] }
// - generisch: { height }
export function extractEmbedHeight(data: unknown): number | undefined {
  let value = data;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!value || typeof value !== "object") return undefined;
  const d = value as {
    type?: string;
    height?: number;
    data?: unknown;
    details?: { height?: number };
    "twttr.embed"?: unknown;
  };
  if (d.type === "MEASURE" && typeof d.details?.height === "number") {
    return d.details.height;
  }
  const twitterCalls = d["twttr.embed"];
  if (twitterCalls) {
    const calls = (
      Array.isArray(twitterCalls) ? twitterCalls : [twitterCalls]
    ) as Array<{ method?: string; params?: Array<{ height?: number }> }>;
    for (const call of calls) {
      if (
        call?.method === "twttr.private.resize" &&
        typeof call.params?.[0]?.height === "number"
      ) {
        return call.params[0].height;
      }
    }
  }
  if (typeof d.height === "number") return d.height;
  return undefined;
}

/**
 * Rein funktionaler Slow-Modus: verbleibende Wartezeit in Sekunden bis die
 * nächste Chat-Nachricht desselben Autors erlaubt ist. 0 bedeutet "freigegeben".
 */
export function slowModeDelta(
  lastCreatedAt: Date | null,
  seconds: number,
  now: Date,
): number {
  if (seconds <= 0 || !lastCreatedAt) return 0;
  const remaining = seconds - (now.getTime() - lastCreatedAt.getTime()) / 1000;
  return remaining > 0 ? Math.ceil(remaining) : 0;
}

export function getEmbedProvider(input: string): string | null {
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase();
  if (!embedAllowedHosts.has(host)) return null;
  if (host.includes("youtube") || host === "youtu.be") return "youtube";
  if (host.includes("vimeo")) return "vimeo";
  if (host === "open.spotify.com") return "spotify";
  if (host.includes("soundcloud")) return "soundcloud";
  if (host.includes("instagram")) return "instagram";
  if (host === "x.com" || host === "www.x.com") return "x";
  if (host.endsWith("twitter.com")) return "x";
  if (
    host === "mixlr.com" ||
    host === "www.mixlr.com" ||
    host === "events.mixlr.com"
  )
    return "mixlr";
  if (
    host === "player.mediadelivery.net" ||
    host === "iframe.mediadelivery.net"
  )
    return "bunny";
  return null;
}

export type Locale = (typeof locales)[number];
export type Role = (typeof roles)[number];
export type EventStatus = (typeof eventStatuses)[number];
export type PostState = (typeof postStates)[number];
export type PostType = (typeof postTypes)[number];
export type MediaState = (typeof mediaStates)[number];

export const hexColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i);
export const slugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

const eventFieldsSchema = z.object({
  title: z.string().trim().min(2).max(160),
  slug: slugSchema,
  description: z.string().trim().max(5_000).default(""),
  venue: z.string().trim().max(180).optional(),
  startsAt: z.coerce.date(),
  // Optional heißt: fehlend ODER null. Das Web-Admin schickt ein leeres
  // Ende-Feld als null (JSON); ohne nullish würde coerce daraus die Epoche
  // (new Date(null) → 1970) machen. Beim PATCH löscht null das Ende.
  endsAt: z.coerce.date().nullish(),
  timezone: z.string().min(1).default("Europe/Berlin"),
  locale: z.enum(locales).default("de"),
  timelineOrder: z.enum(timelineOrders).default("newest"),
  showRelativeTime: z.boolean().default(false),
  showLiveStats: z.boolean().default(false),
  chatEnabled: z.boolean().default(false),
  chatSlowModeSeconds: z.number().int().min(0).max(3_600).default(0),
});

export const createEventSchema = eventFieldsSchema.refine(
  (value) => !value.endsAt || value.endsAt > value.startsAt,
  {
    message: "endsAt must be later than startsAt",
    path: ["endsAt"],
  },
);

export const updateEventSchema = eventFieldsSchema.partial().extend({
  status: z.enum(eventStatuses).optional(),
  isPaused: z.boolean().optional(),
  chatPaused: z.boolean().optional(),
});

export const createPostSchema = z
  .object({
    eventId: z.string().min(1),
    type: z.enum(postTypes),
    content: z.string().trim().max(20_000).default(""),
    mediaAssetId: z.string().optional(),
    videoAssetId: z.string().optional(),
    embedUrl: z.url().optional(),
    byline: z.string().trim().max(100).optional(),
    publishAt: z.coerce.date().optional(),
    clientMutationId: z.string().min(8).max(100),
    pinnedPosition: z.number().int().min(0).max(20).optional(),
  })
  .superRefine((value, context) => {
    if (value.type === "text" && !value.content) {
      context.addIssue({
        code: "custom",
        message: "Text posts require content",
        path: ["content"],
      });
    }
    if (value.type === "image" && !value.mediaAssetId) {
      context.addIssue({
        code: "custom",
        message: "Image posts require mediaAssetId",
        path: ["mediaAssetId"],
      });
    }
    if (value.type === "embed" && !value.embedUrl) {
      context.addIssue({
        code: "custom",
        message: "Embed posts require embedUrl",
        path: ["embedUrl"],
      });
    }
    if (value.type === "video" && !value.videoAssetId) {
      context.addIssue({
        code: "custom",
        message: "Video posts require videoAssetId",
        path: ["videoAssetId"],
      });
    }
  });

export const updatePostSchema = z.object({
  content: z.string().trim().max(20_000).optional(),
  byline: z.string().trim().max(100).nullable().optional(),
  publishAt: z.coerce.date().nullable().optional(),
  publishedAt: z.coerce.date().nullable().optional(),
  pinnedPosition: z.number().int().min(0).max(20).nullable().optional(),
  embedUrl: z.url().optional(),
  state: z.enum(postStates).optional(),
});

export const createSubmissionSchema = z.object({
  eventId: z.string().min(1),
  displayName: z.string().trim().min(1).max(100),
  email: z.email().optional(),
  content: z.string().trim().min(2).max(5_000),
  consent: z.literal(true),
  clientMutationId: z.string().min(8).max(100),
});

export const heartbeatSchema = z.object({
  eventId: z.string().min(1),
  visitorId: z.string().min(8).max(100),
  sessionId: z.string().min(8).max(100),
});

export const chatMessageState = ["visible", "removed"] as const;
export const chatBanKinds = ["user", "ip"] as const;

export const chatReplySnapshotSchema = z.object({
  id: z.string(),
  authorName: z.string(),
  content: z.string(),
});
export interface ChatReplySnapshot {
  id: string;
  authorName: string;
  content: string;
}

export const createChatMessageSchema = z.object({
  content: z.string().trim().min(1).max(500),
  replyToId: z.string().trim().min(1).max(64).optional(),
  nickname: z.string().trim().min(2).max(40).optional(),
  turnstileToken: z.string().min(1).max(2_048).optional(),
  clientMutationId: z.string().min(8).max(100),
});

export const chatTimeoutSchema = z.object({
  userId: z.string().min(1),
  minutes: z.number().int().min(1).max(10_080),
  reason: z.string().trim().max(200).optional(),
});

export const createChatBanSchema = z
  .object({
    kind: z.enum(chatBanKinds),
    value: z.string().trim().min(1).max(200),
    eventId: z.string().min(1).nullish(),
    reason: z.string().trim().max(200).optional(),
    minutes: z.number().int().min(1).max(10_080).nullish(),
  })
  .refine((value) => value.kind !== "ip" || !value.minutes, {
    message: "IP bans are permanent; use user timeouts instead",
    path: ["minutes"],
  });

export const brandingSchema = z.object({
  primaryColor: hexColorSchema,
  secondaryColor: hexColorSchema,
  logoUrl: z.url().nullable().optional(),
  locale: z.enum(locales),
  timezone: z.string().min(1),
  submissionsEnabled: z.boolean(),
  publicStatsEnabled: z.boolean(),
  isPaused: z.boolean(),
  watermarkEnabled: z.boolean(),
  watermarkPosition: z.enum(watermarkPositions),
  watermarkOpacity: z.number().min(0.1).max(1),
});

export const alertBannerSchema = z.object({
  eventId: z.string().min(1),
  text: z.string().trim().min(1).max(500),
  tone: z.enum(["info", "warning", "critical"]).default("info"),
  active: z.boolean().default(true),
});

export const stickyMediaSchema = z.object({
  eventId: z.string().min(1),
  embedUrl: z.url(),
  title: z.string().trim().min(1).max(180),
  active: z.boolean().default(true),
});

export const pluginInstallSchema = z.object({
  pluginId: z.string().min(2).max(100),
  eventId: z.string().min(1).nullish(),
  enabled: z.boolean().default(true),
  config: z.record(z.string(), z.unknown()).default({}),
});

export const apiErrorSchema = z.object({
  error: z.string(),
  code: z.string(),
  details: z.unknown().optional(),
});

export type CreateEventInput = z.infer<typeof createEventSchema>;
export type UpdateEventInput = z.infer<typeof updateEventSchema>;
export type CreatePostInput = z.infer<typeof createPostSchema>;
export type UpdatePostInput = z.infer<typeof updatePostSchema>;
export type CreateSubmissionInput = z.infer<typeof createSubmissionSchema>;
export type HeartbeatInput = z.infer<typeof heartbeatSchema>;
export type CreateChatMessageInput = z.infer<typeof createChatMessageSchema>;
export type ChatTimeoutInput = z.infer<typeof chatTimeoutSchema>;
export type CreateChatBanInput = z.infer<typeof createChatBanSchema>;
export type BrandingInput = z.infer<typeof brandingSchema>;

export interface MediaDerivative {
  key: string;
  url: string;
  width: number;
  height: number;
  format: "avif" | "webp" | "jpeg" | "original";
}

export interface PublicPostVideo {
  state: "uploading" | "queued" | "processing" | "ready" | "failed" | "removed";
  title: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
}

export interface PublicTenant {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  locale: Locale;
  timezone: string;
  isPaused: boolean;
  submissionsEnabled: boolean;
  chatEnabled: boolean;
  legal: {
    imprintUrl?: string;
    privacyUrl?: string;
  };
}

export interface PublicEvent {
  id: string;
  slug: string;
  title: string;
  description: string;
  venue: string | null;
  startsAt: string;
  endsAt: string | null;
  status: EventStatus;
  timezone: string;
  timelineOrder: "newest" | "oldest";
  showRelativeTime: boolean;
  showLiveStats: boolean;
  chatEnabled: boolean;
  chatPaused: boolean;
}

export interface PublicPost {
  id: string;
  type: PostType;
  content: string;
  byline: string | null;
  publishedAt: string;
  pinnedPosition: number | null;
  media: null | {
    alt: string | null;
    credit: string | null;
    derivatives: MediaDerivative[];
  };
  video: PublicPostVideo | null;
  embed: null | {
    url: string;
    provider: string;
    title: string;
    html: string | null;
    thumbnailUrl: string | null;
    consentCategory: "external-media";
  };
}

export interface TimelinePayload {
  tenant: PublicTenant;
  event: PublicEvent;
  posts: PublicPost[];
  alert: null | {
    id: string;
    text: string;
    tone: "info" | "warning" | "critical";
  };
  stickyMedia: null | {
    id: string;
    title: string;
    embed: null | {
      url: string;
      provider: string;
      html: string | null;
      consentCategory: "external-media";
    };
  };
  plugins: Array<{ id: string; slot: string; payload: unknown }>;
  serverTime: string;
  revision: string;
  productHost: string;
}

export interface StatsPoint {
  bucket: string;
  uniqueVisitors: number;
  peakOnline: number;
}

export interface EventStats {
  onlineNow: number;
  totalUnique: number;
  points: StatsPoint[];
}

export interface ChatMessage {
  id: string;
  eventId: string;
  authorName: string;
  authorUserId: string | null;
  content: string;
  createdAt: string;
  // Antwort-Bezug: eingefrorener Schnappschuss der bezogenen Nachricht,
  // damit das Zitat den Moderations-Entzug überlebt.
  replyTo?: ChatReplySnapshot | null;
  // Redaktion-Badge: Autor*in ist Owner/Admin/Editor der Tenant-Organisation.
  editor?: boolean;
}

export interface ChatMessageForAdmin extends ChatMessage {
  authorIpHash: string | null;
  state: (typeof chatMessageState)[number];
  removedAt: string | null;
  timedOutUntil: string | null;
}

export interface ChatState {
  chatEnabled: boolean;
  chatPaused: boolean;
  messages: ChatMessage[];
  captcha: {
    required: boolean;
    siteKey: string | null;
  };
}

export type ChatBanKind = (typeof chatBanKinds)[number];

export interface ChatBan {
  id: string;
  eventId: string | null;
  kind: ChatBanKind;
  value: string;
  reason: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export const newMutationId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Minimal shape needed to sort events and pick the current one. */
export interface PickableEvent {
  id: string;
  status: EventStatus;
  startsAt: string;
  endsAt: string | null;
}

/**
 * Events a desk can work with (everything except archived and removed),
 * ordered by start date, soonest first.
 */
export function sortOpenEvents<T extends PickableEvent>(events: T[]): T[] {
  return events
    .filter(
      (event) => event.status !== "archived" && event.status !== "removed",
    )
    .sort(
      (first, second) =>
        Date.parse(first.startsAt) - Date.parse(second.startsAt),
    );
}

/**
 * Picks the event a desk should show by default: live first, then the next
 * scheduled event by start date, then a still-running paused event, then the
 * most recent open event.
 */
export function pickCurrentEvent<T extends PickableEvent>(
  events: T[],
): T | null {
  const open = sortOpenEvents(events);
  const live = open.find((event) => event.status === "live");
  if (live) return live;
  const now = Date.now();
  const next = open.find(
    (event) =>
      event.status === "scheduled" && Date.parse(event.startsAt) >= now,
  );
  if (next) return next;
  const paused = open.find(
    (event) =>
      event.status === "paused" &&
      (!event.endsAt || Date.parse(event.endsAt) >= now),
  );
  if (paused) return paused;
  return open[open.length - 1] ?? null;
}
